import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AccountCard } from "../components/profile/AccountCard";
import { ApiKey } from "../redux/reducers/userSlice";
import { ApiKeyList } from "../components/profile/ApiKeyList";
import { Button, InputField, Text, View } from "../components/base";
import { Dialog } from "../components/base/Dialog";
import { Icon } from "../components/base/Icon";
import { PageLayout } from "../components/PageLayout";
import { PhoneNumberCard } from "../components/profile/PhoneNumberCard";
import { OrganizationSettingsTab } from "../components/profile/OrganizationSettingTab";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppSelector } from "../redux";
import { useNavigation } from "../hooks/useNavigation";

export const ProfilePage = () => {
  const user = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [description, setDescription] = useState("");
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const createApiKeyRequest = useApiRequest<{ data: ApiKey }>();
  const getApiKeysRequest = useApiRequest<{ data: ApiKey[] }>();
  const revokeApiKeyRequest = useApiRequest();

  const onClickCreateApiKey = async () => {
    const response = await createApiKeyRequest.fetch("/v1/users/api-keys", {
      method: "POST",
      body: { description },
    });

    if (response?.data) {
      setNewApiKey(response.data?.data?.token || "");
      setShowApiKeyDialog(true);
      setShowDescriptionDialog(false);
      setDescription("");
      getApiKeysRequest.fetch("/v1/users/api-keys");
    }
  };

  const onClickDeleteApiKey = async (keyId: string) => {
    await revokeApiKeyRequest.fetch(`/v1/users/api-keys/${keyId}`, {
      method: "DELETE",
    });
    getApiKeysRequest.fetch("/v1/users/api-keys");
  };

  const onClickCopyApiKey = async () => {
    await navigator.clipboard.writeText(newApiKey);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 1500);
  };

  useEffect(() => {
    getApiKeysRequest.fetch("/v1/users/api-keys");
  }, []);

  return (
    <PageLayout
      title="Profile"
      description="Manage your account settings and API keys"
      headerAction={
        <button
          onClick={() => navigation.navigate("/signout")}
          className="flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors text-sm font-medium"
        >
          <Icon
            name="logout"
            size={14}
            className="text-red-500 dark:text-red-400"
          />
          Sign out
        </button>
      }
    >
      <View className="flex flex-col lg:flex-row gap-6 mt-6">
        {/* Left column - 2/3 */}
        <View className="flex flex-col gap-6 flex-1 lg:w-2/3 min-w-0">
          <AccountCard email={user?.email} />
          {user?.isAdmin && (
            <OrganizationSettingsTab title="Notifications">
              <button
                onClick={() => navigate("/notification-preferences")}
                className="flex flex-row items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left w-full group"
              >
                <View className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors shrink-0">
                  <Icon
                    name="notification"
                    size={16}
                    className="text-gray-500 dark:text-gray-400"
                  />
                </View>
                <View className="flex flex-col min-w-0 flex-1">
                  <Text className="text-sm font-medium text-gray-900 dark:text-white">Notification Preferences</Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">Manage your notification settings</Text>
                </View>
                <Icon
                  name="chevron-right"
                  size={14}
                  className="text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors shrink-0"
                />
              </button>
            </OrganizationSettingsTab>
          )}
        </View>

        {/* Right column - 1/3 */}
        <View className="flex flex-col gap-6 w-full lg:w-1/3 shrink-0">
          <ApiKeyList
            apiKeys={getApiKeysRequest.data?.data || []}
            onCreateKey={() => setShowDescriptionDialog(true)}
            onDeleteKey={onClickDeleteApiKey}
            isLoading={getApiKeysRequest.isLoading}
          />
          <PhoneNumberCard phoneNumberId={user?.phoneNumberId} />
        </View>
      </View>

      <Dialog
        isOpen={showDescriptionDialog}
        onClose={() => setShowDescriptionDialog(false)}
        title="New API Key"
      >
        <View className="p-4 space-y-4">
          <Text variant="body">Enter a description for your new API key:</Text>
          <InputField
            value={description}
            onChange={(description) => setDescription(description)}
            placeholder="Enter key description..."
            className="w-full text-slate-700"
          />
          <Button
            onClick={onClickCreateApiKey}
            isDisabled={!description || createApiKeyRequest.isLoading}
            className="bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white px-4 py-2 w-full"
          >
            {createApiKeyRequest.isLoading ? "Generating..." : "Generate API Key"}
          </Button>
        </View>
      </Dialog>

      <Dialog
        isOpen={showApiKeyDialog}
        onClose={() => setShowApiKeyDialog(false)}
        title="Your New API Key"
      >
        <View className="flex flex-col p-4">
          <Text
            variant="body"
            className="mb-4"
          >
            Please save this API key - you won't be able to see it again!
          </Text>
          <View className="flex items-center space-x-2">
            <InputField
              value={newApiKey}
              readOnly
              className="flex-1"
            />
            <Button
              onClick={onClickCopyApiKey}
              variant="brand"
              className="whitespace-nowrap"
            >
              {showCopySuccess ? "Copied!" : "Copy Key"}
            </Button>
          </View>
        </View>
      </Dialog>
    </PageLayout>
  );
};
